import { useState, useCallback, useEffect, useRef } from 'react';
import type { Node as RFNode, Edge as RFEdge } from '@xyflow/react';
import { useReactFlow } from '@xyflow/react';
import { Dropdown, Icon, icon } from '@nula/core-ui';
import { useNodeActions } from '@/features/canvas/engine/wiring/contexts/node-actions-context';
import { useEdgeActions } from '@/features/canvas/engine/wiring/contexts/edge-actions-context';
import { useCanvasDoc } from '@/features/canvas/engine/wiring/contexts/canvas-doc-context';
import { useCanvasBinding } from '@/features/canvas/engine/wiring/contexts/canvas-binding-context';
import { useScreenToCanvasCoords } from '@/features/canvas/engine/hooks/use-screen-to-canvas-coords';
import { deserializeIntoYjs } from '@/features/canvas/engine/primitives/clipboard';
import { isDesktop } from '@/lib/platform';
import { canvasShortcuts } from '@/features/canvas/utils/canvas-shortcuts';
import { FRAME_ACTIONS } from '@/features/canvas/engine/selection/wiring/actions/frame-actions';
import { NODE_ACTIONS, type NodeActionContext } from '@/features/canvas/engine/selection/wiring/actions/node-actions';
import { EDGE_ACTIONS, type EdgeActionContext } from '@/features/canvas/engine/selection/wiring/actions/edge-actions';
import { MULTI_ACTIONS, type MultiActionContext } from '@/features/canvas/engine/selection/wiring/actions/multi-actions';

// ── Shared helpers ────────────────────────────────────────────────────────────

function ic(name: string) {
  return <Icon name={name} size={icon.md} />;
}

function isCanvasJson(text: string): boolean {
  if (!text.startsWith('{')) return false;
  try {
    const p = JSON.parse(text);
    return p !== null && typeof p === 'object' && 'nodes' in p;
  } catch {
    return false;
  }
}

type MenuState =
  | { kind: 'node'; nodeId: string; nodeType: string; locked: boolean; pos: { x: number; y: number } }
  | { kind: 'multi'; ids: string[]; pos: { x: number; y: number } }
  | { kind: 'edge'; edgeId: string; locked: boolean; pos: { x: number; y: number } }
  | { kind: 'pane'; pos: { x: number; y: number } }
  | null;

// ── Handler hook ──────────────────────────────────────────────────────────────

export interface CanvasContextMenuHandlers {
  onNodeContextMenu: (e: React.MouseEvent, node: RFNode) => void;
  onEdgeContextMenu: (e: React.MouseEvent, edge: RFEdge) => void;
  onPaneContextMenu: (e: React.MouseEvent | MouseEvent) => void;
}

/**
 * Returns stable RF-compatible handlers for onNodeContextMenu /
 * onEdgeContextMenu / onPaneContextMenu. Pass the returned ref into
 * <CanvasContextMenu> so it can register its open callbacks.
 */
export function useCanvasContextMenu(): {
  handlers: CanvasContextMenuHandlers;
  menuRef: React.MutableRefObject<{
    openNode: (e: React.MouseEvent, node: RFNode) => void;
    openEdge: (e: React.MouseEvent, edge: RFEdge) => void;
    openPane: (e: React.MouseEvent | MouseEvent) => void;
  }>;
} {
  const menuRef = useRef({
    openNode: (_e: React.MouseEvent, _node: RFNode) => {},
    openEdge: (_e: React.MouseEvent, _edge: RFEdge) => {},
    openPane: (_e: React.MouseEvent | MouseEvent) => {},
  });

  const handlers: CanvasContextMenuHandlers = {
    onNodeContextMenu: useCallback((e, node) => {
      menuRef.current.openNode(e, node);
    }, []),
    onEdgeContextMenu: useCallback((e, edge) => {
      menuRef.current.openEdge(e, edge);
    }, []),
    onPaneContextMenu: useCallback((e) => {
      menuRef.current.openPane(e);
    }, []),
  };

  return { handlers, menuRef };
}

// ── CanvasContextMenu ─────────────────────────────────────────────────────────

export interface CanvasContextMenuProps {
  menuRef: React.MutableRefObject<{
    openNode: (e: React.MouseEvent, node: RFNode) => void;
    openEdge: (e: React.MouseEvent, edge: RFEdge) => void;
    openPane: (e: React.MouseEvent | MouseEvent) => void;
  }>;
}

/**
 * Manages all canvas right-click menus (node, multi-select, pane).
 * Must mount inside <ReactFlow>. Wire useCanvasContextMenu() handlers onto
 * <ReactFlow onNodeContextMenu> and <ReactFlow onPaneContextMenu>.
 */
export function CanvasContextMenu({ menuRef }: CanvasContextMenuProps) {
  const actions = useNodeActions();
  const edgeActions = useEdgeActions();
  const { getNodes, setNodes } = useReactFlow();
  const screenToCanvas = useScreenToCanvasCoords();
  const doc = useCanvasDoc();
  const binding = useCanvasBinding();
  const [menu, setMenu] = useState<MenuState>(null);

  const handleClose = useCallback(() => setMenu(null), []);

  // Register open callbacks into the ref so the stable handlers above can call them.
  useEffect(() => {
    menuRef.current = {
      openNode: (e, node) => {
        e.preventDefault();
        e.stopPropagation();
        const selectedIds = getNodes().filter(n => n.selected).map(n => n.id);
        if (selectedIds.length >= 2 && selectedIds.includes(node.id)) {
          setMenu({ kind: 'multi', ids: selectedIds, pos: { x: e.clientX, y: e.clientY } });
        } else {
          setMenu({
            kind: 'node',
            nodeId: node.id,
            nodeType: node.type ?? '',
            locked: (node.data as Record<string, unknown>).locked === true,
            pos: { x: e.clientX, y: e.clientY },
          });
        }
      },
      openEdge: (e, edge) => {
        e.preventDefault();
        e.stopPropagation();
        const data = (edge.data ?? {}) as Record<string, unknown>;
        setMenu({
          kind: 'edge',
          edgeId: edge.id,
          locked: data.locked === true,
          pos: { x: e.clientX, y: e.clientY },
        });
      },
      openPane: (e) => {
        e.preventDefault();
        setMenu({ kind: 'pane', pos: { x: (e as MouseEvent).clientX, y: (e as MouseEvent).clientY } });
      },
    };
  }, [menuRef, getNodes]);

  // Closure: the Dropdown's own onOpenChange(false) handler (wired to
  // handleClose below) fires when the user clicks outside or selects a
  // menu item, so we don't need a parallel document-mousedown listener.
  // The previous listener actually broke the z-order menu items: a
  // browser fires mousedown before click, so the listener would
  // setMenu(null) → unmount the Dropdown → the menu item's onClick
  // never landed. Removing it here lets Bring-to-Front / Send-to-Back
  // (and the rest) actually invoke their handlers.

  if (!menu) return null;
  // Edge menu uses edgeActions; node/multi/pane use actions.
  if (menu.kind === 'edge') {
    if (!edgeActions) return null;
  } else if (!actions) {
    return null;
  }

  const sc = canvasShortcuts;
  let items: { key: string; label: React.ReactNode; icon?: React.ReactNode; extra?: string; danger?: boolean; type?: 'divider' | 'item'; onClick?: () => void }[];

  if (menu.kind === 'node') {
    const { nodeId, nodeType, locked } = menu;
    const a = actions!;
    // Frames don't expose Copy / Duplicate — duplicating a frame would
    // also need to deep-clone its children to be useful, which is a
    // bigger surface than the v1 action set covers. Toolbar matches.
    const isFrame = nodeType === 'frame';
    // Pull Ungroup from the shared FRAME_ACTIONS registry so renaming
    // the registry entry reflows here automatically. Frame-only.
    const ungroupAction = isFrame ? FRAME_ACTIONS.find((x) => x.id === 'ungroup') : undefined;

    // Build the NodeActionContext once and let NODE_ACTIONS' available()
    // + run() drive every standard item. Per-action state-aware label/
    // icon (Lock ↔ Unlock) flows through labelFor/iconFor.
    const nodeCtx: NodeActionContext = {
      nodeId,
      locked,
      onCopy: a.onCopy,
      onDuplicate: a.onDuplicate,
      onBringToFront: a.onBringToFront,
      onBringForward: a.onBringForward,
      onSendBackward: a.onSendBackward,
      onSendToBack: a.onSendToBack,
      onToggleLock: a.onToggleLock,
      onDelete: a.onDelete,
    };

    // Map registry → menu items (still kind-shaped for the Dropdown).
    // Frame surfaces skip copy/duplicate by id-based filter — preserves
    // the existing nodeType-based prune.
    // Map registry shortcut IDs → canvasShortcuts label fns.
    const shortcutFor = (id: string): string | undefined => {
      switch (id) {
        case 'copy': return sc.copy();
        case 'duplicate': return sc.duplicate();
        case 'bring-to-front': return sc.bringToFront();
        case 'bring-forward': return sc.bringForward();
        case 'send-backward': return sc.sendBackward();
        case 'send-to-back': return sc.sendToBack();
        case 'lock': return locked ? sc.unlock() : sc.lock();
        case 'delete': return sc.delete();
        default: return undefined;
      }
    };

    const registryItems = NODE_ACTIONS
      .filter((act) => act.available(nodeCtx))
      .filter((act) => !isFrame || (act.id !== 'copy' && act.id !== 'duplicate'))
      .map((act) => {
        const label = act.labelFor ? act.labelFor(nodeCtx) : act.label;
        const iconName = act.iconFor ? act.iconFor(nodeCtx) : act.icon;
        return {
          key: act.id,
          label,
          icon: ic(iconName),
          extra: shortcutFor(act.id),
          danger: act.danger,
          onClick: () => { act.run(nodeCtx); handleClose(); },
        } as { key: string; label: string; icon: React.ReactNode; extra?: string; danger?: boolean; onClick: () => void };
      });

    // Visual structure: [copy/dup block] / [z-order block] / [lock + ungroup + delete].
    // Insert dividers between groups by walking the items and
    // inserting at the boundaries between known groups.
    const grouped: typeof items = [];
    const groupOf = (id: string): 'copy' | 'zorder' | 'destructive' => {
      if (id === 'copy' || id === 'duplicate') return 'copy';
      if (id === 'bring-to-front' || id === 'bring-forward' ||
          id === 'send-backward' || id === 'send-to-back') return 'zorder';
      return 'destructive';
    };
    let lastGroup: 'copy' | 'zorder' | 'destructive' | null = null;
    let dividerIdx = 0;
    for (const it of registryItems) {
      const g = groupOf(it.key);
      if (lastGroup !== null && g !== lastGroup) {
        grouped.push({ key: `div${dividerIdx++}`, label: '', type: 'divider' });
      }
      // Insert Ungroup right before Delete in the destructive cluster.
      // Locked frames hide Ungroup (matches FRAME_ACTIONS.ungroup.available),
      // matching the previous locked-branch behavior where only Unlock +
      // Delete survived.
      if (it.key === 'delete' && ungroupAction && !locked) {
        grouped.push({
          key: 'ungroup',
          label: ungroupAction.label,
          icon: ic(ungroupAction.icon),
          onClick: () => { a.onUngroup(nodeId); handleClose(); },
        });
        grouped.push({ key: `div${dividerIdx++}`, label: '', type: 'divider' });
      }
      grouped.push(it);
      lastGroup = g;
    }
    items = grouped;
  } else if (menu.kind === 'multi') {
    const { ids } = menu;
    const a = actions!;

    const multiCtx: MultiActionContext = {
      ids,
      onCopyMany: a.onCopyMany,
      onDuplicateMany: a.onDuplicateMany,
      onDeleteMany: a.onDeleteMany,
      onGroupMany: a.onGroupMany,
    };

    const multiShortcutFor = (id: string): string | undefined => {
      switch (id) {
        case 'copy': return sc.copy();
        case 'duplicate': return sc.duplicate();
        case 'delete': return sc.delete();
        // Group has no shortcut wired in canvasShortcuts yet — the
        // existing useGroupShortcut keyboard path uses Cmd+G but the
        // shortcut display string isn't centralized. Return undefined
        // so the menu item shows no extra-text suffix; future work
        // can register the shortcut in canvasShortcuts and surface it
        // here without the menu needing further changes.
        default: return undefined;
      }
    };

    const multiRegistryItems = MULTI_ACTIONS
      .filter((act) => act.available(multiCtx))
      .map((act) => ({
        key: act.id,
        label: act.label,
        icon: ic(act.icon),
        extra: multiShortcutFor(act.id),
        danger: act.danger,
        onClick: () => { act.run(multiCtx); handleClose(); },
      } as { key: string; label: string; icon: React.ReactNode; extra?: string; danger?: boolean; onClick: () => void }));

    // Visual structure: [copy + duplicate] / [delete]. Insert one
    // divider before delete so destructive sits in its own cluster.
    const multiGrouped: typeof items = [];
    let dividerIdx = 0;
    for (const it of multiRegistryItems) {
      if (it.key === 'delete' && multiGrouped.length > 0) {
        multiGrouped.push({ key: `div${dividerIdx++}`, label: '', type: 'divider' });
      }
      multiGrouped.push(it);
    }
    items = multiGrouped;
  } else if (menu.kind === 'edge') {
    const { edgeId, locked } = menu;
    const ea = edgeActions!;

    const edgeCtx: EdgeActionContext = {
      edgeId,
      locked,
      onBringToFront: ea.onBringToFront,
      onBringForward: ea.onBringForward,
      onSendBackward: ea.onSendBackward,
      onSendToBack: ea.onSendToBack,
      onToggleLock: ea.onToggleLock,
      onDelete: ea.onDelete,
    };

    const edgeShortcutFor = (id: string): string | undefined => {
      switch (id) {
        case 'bring-to-front': return sc.bringToFront();
        case 'bring-forward': return sc.bringForward();
        case 'send-backward': return sc.sendBackward();
        case 'send-to-back': return sc.sendToBack();
        case 'delete': return sc.delete();
        default: return undefined;
      }
    };

    const edgeRegistryItems = EDGE_ACTIONS
      .filter((act) => act.available(edgeCtx))
      .map((act) => {
        const label = act.labelFor ? act.labelFor(edgeCtx) : act.label;
        const iconName = act.iconFor ? act.iconFor(edgeCtx) : act.icon;
        return {
          key: act.id,
          label,
          icon: ic(iconName),
          extra: edgeShortcutFor(act.id),
          danger: act.danger,
          onClick: () => { act.run(edgeCtx); handleClose(); },
        } as { key: string; label: string; icon: React.ReactNode; extra?: string; danger?: boolean; onClick: () => void };
      });

    // Visual structure: [z-order block] / [lock] / [delete]. Insert
    // dividers at group transitions, same approach as the node branch.
    const edgeGroupOf = (id: string): 'zorder' | 'lock' | 'delete' => {
      if (id === 'bring-to-front' || id === 'bring-forward' ||
          id === 'send-backward' || id === 'send-to-back') return 'zorder';
      if (id === 'lock') return 'lock';
      return 'delete';
    };
    const edgeGrouped: typeof items = [];
    let lastGroup: 'zorder' | 'lock' | 'delete' | null = null;
    let dividerIdx = 0;
    for (const it of edgeRegistryItems) {
      const g = edgeGroupOf(it.key);
      if (lastGroup !== null && g !== lastGroup) {
        edgeGrouped.push({ key: `div${dividerIdx++}`, label: '', type: 'divider' });
      }
      edgeGrouped.push(it);
      lastGroup = g;
    }
    items = edgeGrouped;
  } else {
    const capturedPos = menu.pos;
    const handlePaste = () => {
      handleClose();
      if (!doc) return;
      const pasteAtPos = screenToCanvas(capturedPos.x, capturedPos.y);
      const readClipboard = isDesktop
        ? import('@tauri-apps/plugin-clipboard-manager').then(m => m.readText())
        : navigator.clipboard.readText();
      readClipboard.then((text) => {
        if (text && isCanvasJson(text)) deserializeIntoYjs(text, doc, pasteAtPos, undefined, binding);
      }).catch(() => { document.execCommand('paste'); });
    };
    const handleSelectAll = () => {
      actions?.onSelectAll();
      setNodes(getNodes().map(n => ({ ...n, selected: true })));
      handleClose();
    };
    items = [
      { key: 'paste',     label: 'Paste',      icon: ic('content_paste'), extra: sc.paste(),     onClick: handlePaste },
      { key: 'div1', label: '', type: 'divider' },
      { key: 'selectall', label: 'Select all', icon: ic('select_all'),    extra: sc.selectAll(), onClick: handleSelectAll },
    ];
  }

  return (
    <Dropdown
      menu={{ items }}
      open
      onOpenChange={(o) => { if (!o) handleClose(); }}
      menuPosition={menu.pos}
    >
      <span />
    </Dropdown>
  );
}
